import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { palette, ChartTooltip, ChartEmpty } from "./ChartTheme";

const DEFAULT_COLORS = palette.series;

/**
 * Minimal donut. data: [{ name, value }] — unchanged props/shape.
 * Redesign: slightly thicker ring, a total figure centered in the
 * hole (the classic donut affordance), and legend rendered as small
 * pill chips instead of Recharts' default markers.
 */
const renderLegend = ({ payload }) => (
  <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1.5">
    {payload.map((entry) => (
      <span
        key={entry.value}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-neutral-400"
      >
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: entry.color }}
        />
        {entry.value}
      </span>
    ))}
  </div>
);

const DonutChart = ({
  data = [],
  colors = DEFAULT_COLORS,
  height = 200,
  innerRadius = 52,
  outerRadius = 76,
}) => {
  if (!data?.length) {
    return <ChartEmpty height={height} />;
  }

  const total = data.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  return (
    <div style={{ width: "100%", height }} className="relative">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="46%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={3}
            cornerRadius={4}
            strokeWidth={0}
            animationDuration={350}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={colors[i % colors.length]} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
          <Legend content={renderLegend} verticalAlign="bottom" />
        </PieChart>
      </ResponsiveContainer>
      {/* Centered total — purely presentational, sits inside the ring */}
      <div
        className="pointer-events-none absolute left-0 right-0 flex flex-col items-center justify-center"
        style={{ top: "calc(46% - 1px)", transform: "translateY(-50%)" }}
      >
        <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-neutral-100">
          {total.toLocaleString()}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-neutral-500">
          Total
        </span>
      </div>
    </div>
  );
};

export default DonutChart;